import React, { useEffect, useState } from "react";

import gsap from "gsap";
import ScrollTrigger from "gsap/dist/ScrollTrigger";

import ClockIcon from "../../../assets/icons/front-pages/clock.svg";
import MessageTextIcon from "../../../assets/icons/front-pages/message-text.svg";
import MessagesIcon from "../../../assets/icons/front-pages/messages.svg";
import MessageNotifIcon from "../../../assets/icons/front-pages/message-notif.svg";
import HashtagIcon from "../../../assets/icons/front-pages/hashtag.svg";
import CardsIcon from "../../../assets/icons/front-pages/cards.svg";
import WalletIcon from "../../../assets/icons/front-pages/wallet-minus.svg";
import InvoiceIcon from "../../../assets/icons/front-pages/invoice.svg";
import ClipboardTick from "../../../assets/icons/front-pages/clipboard-tick.svg";
import RefreshCircleIcon from "../../../assets/icons/front-pages/refresh-circle.svg";
import SearchStatusIcon from "../../../assets/icons/front-pages/search-status.svg";
import CoinIcon from "../../../assets/icons/front-pages/coin.svg";
import classNames from "classnames";
import PagesLoaderContext from "../../../contexts/page-loader-context";
import AnimatedHeaderText from "../commons/animated-header-text";
import CircularProgress from "../../ui/circular-progress";
import LazyImage from "../../lazy-image";
import { getStyles } from "../../../assets/js/utils/functions";
import { getSizedImage } from "../../../assets/js/utils/utils";
import useScreenSize, { BreakPoint } from "../../hooks/useScreenSize";

const ReasonsToUseCatlog = () => {
  const { animationHasPlayed } = PagesLoaderContext.useContainer();
  const [animProgress, setAnimProgress] = useState(0);
  const featuresCount = features.length;
  const { screen } = useScreenSize();

  useEffect(() => {
    if (!animationHasPlayed) return;

    const stepsWrapper = document.querySelector(".features-wrapper");
    const steps = Array.from(document.querySelectorAll("li.feature"));
    const listContainer: HTMLUListElement = document.querySelector(".list-container");

    gsap.registerPlugin(ScrollTrigger);
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: stepsWrapper,
        pin: true,
        start: "-5% top",
        pinSpacing: true,
        scrub: true,
        end: "300% top",
        snap: 1 / featuresCount,
        anticipatePin: 0.1,
      },
      onUpdate: () => {
        const progress = tl.progress();

        setAnimProgress(progress);

        const activeFeature = Math.floor((progress - 0.01) * featuresCount);

        const { scrollHeight } = listContainer;
        const actualHeight = listContainer.parentElement.clientHeight;
        const unitScroll = (scrollHeight - actualHeight + (steps[0] as HTMLLIElement).clientHeight) / featuresCount;

        //change the translateY of the cointainer by precalculated units

        if (activeFeature > -1) {
          listContainer.style.transform = `translateY(${-(unitScroll * activeFeature)}px)`;
        }
      },
    });

    steps.forEach((step) => {
      tl.to(step, {
        duration: 3,
      });
    });

    return () => {
      tl.kill();
    };
  }, [animationHasPlayed]);

  const isActive = (index: number) => animProgress > 0 && Math.floor((animProgress - 0.01) * featuresCount) === index; //0.25 = 1/steps //0.01 is offset for when progress reaches boundaries, since we can't manually check for < the actual progress
  const isPast = (index: number) => index < Math.floor((animProgress - 0.01) * featuresCount);

  return (
    <section className="bg-primary-pastel body-padding body-y-padding">
      <div className="container">
        <AnimatedHeaderText
          className="mb-10 sm:mb-12.5 lg:mb-15"
          series={[
            ["Catlog", "was", "built", "for"],
            ["businesses", "like", "yours"],
          ]}
        />
        <div className="w-full flex flex-col-reverse md:grid md:grid-cols-[55%,45%] lg:grid-cols-[62%,38%] h-[92vh] features-wrapper hide-for-observer">
          <div className="md:pr-8 md:max-w-[680px] flex flex-col mt-5 sm:mt-8 md:mt-0">
            <figure className="bg-primary-pastel rounded-15 sm:rounded-3xl lg:rounded-30  md:flex-1 overflow-hidden h-[65vw] sm:h-[52vw] md:h-[unset] relative safari-overflow-fix">
              <LazyImage
                src={getSizedImage(
                  "https://res.cloudinary.com/catlog/image/upload/v1670478522/front-pages/woman-using-laptop.webp",
                  { [BreakPoint.xs]: "w_600", default: "w_900" },
                  screen
                )}
                alt=""
                loaderClasses="rounded-15 sm:rounded-3xl lg:rounded-30"
                className="h-full w-full absolute top-0 left-0 object-[0% 25%] md:object-center rounded-15 sm:rounded-3xl lg:rounded-30 object-cover image-appear"
              />
              <div className="flex items-center absolute top-5 left-5 bg-white p-1 rounded-5">
                <div className="text-accent-green-500">
                  <CircularProgress progress={animProgress} baseColor="#F9F9F9" width={24} outline={4} />
                </div>
                <span className="text-black font-bold text-1xs inline-block ml-1.25">
                  {Math.floor((animProgress - 0.01) * featuresCount) + 1}/{featuresCount}
                </span>
              </div>
            </figure>
          </div>
          <div className="h-full overflow-hidden flex-1 " style={{ scrollBehavior: "smooth" }}>
            <ul className="w-full flex flex-col space-y-5 sm:space-y-6.25 mb-7.5 sm:mb-10 md:mb-0 list-container transition-transform ease-out duration-300">
              {features.map((feature, index) => (
                <li
                  className="bg-white py-5 px-5.5 sm:py-6 sm:px-6.25 xl:py-6.25 xl:px-7.5 rounded-15 sm:rounded-20 lg:rounded-3xl border border-grey-fields-100 feature anim-word-up"
                  key={index}
                  style={getStyles({ "--delay": `${(index % 4) * 0.25}s`, "--from": "15%", "--duration": "1s" })}
                >
                  <div className="flex items-start justify-between">
                    <figure className="h-12.5 w-12.5 sm:h-15 sm:w-15 lg:h-16 lg:w-16 xl:h-17.5 xl:w-17.5 bg-grey-fields-200 rounded-full flex items-center justify-center">
                      <div className="w-1/2 text-accent-yellow-500">
                        <feature.icon />
                      </div>
                    </figure>
                    {/* prettier-ignore */}
                    <svg className={classNames("w-6 transform transition-all duration-200 origin-center ease-in-out", {
                        "opacity-0 scale-0": !isActive(index),
                        "opacity-100 scale-100": isActive(index)
                      })} viewBox="0 0 24 24" fill="none">
                      <rect width="24" height="24" rx="12" fill="#39B588"/>
                      <path d="M10.2951 16.5C10.055 16.5 9.82691 16.3995 9.65883 16.2238L6.26113 12.6702C5.91296 12.3061 5.91296 11.7033 6.26113 11.3392C6.6093 10.9751 7.18559 10.9751 7.53377 11.3392L10.2951 14.2272L16.4662 7.77311C16.8144 7.40896 17.3907 7.40896 17.7389 7.77311C18.087 8.13725 18.087 8.73997 17.7389 9.10412L10.9315 16.2238C10.7634 16.3995 10.5353 16.5 10.2951 16.5Z" fill="white"/>
                    </svg>
                  </div>
                  <p
                    className={`mt-3.75 sm:mt-5 xl:mt-6 font-medium sub-text lg:max-w-[380px] transition-all ease-out duration-300 ${
                      isActive(index) ? "text-black" : "text-grey-muted"
                    }`}
                  >
                    {feature.description}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

const features = [
  {
    icon: ClockIcon,
    description: "Catlog is really easy to setup, all it takes is 5 minutes",
  },
  {
    icon: MessagesIcon,
    description: "With Catlog you won’t need to go back and forth with customers in your DMs",
  },
  {
    icon: MessageNotifIcon,
    description: "Orders placed on your store comes as a message on Whatsapp",
  },
  {
    icon: MessageTextIcon,
    description: "You receive the details of the items ordered and delivery details as one message",
  },
  {
    icon: WalletIcon,
    description: "Catlog gives you a free bank account for your business",
  },
  {
    icon: CardsIcon,
    description: "You can easily collect payments with cards, transfers & Split payments",
  },
  {
    icon: ClipboardTick,
    description: "Catlog helps you keep track of your sales, so you always have accurate records",
  },
  {
    icon: RefreshCircleIcon,
    description: "With Catlog you’ll never mix up orders when it’s time to deliver",
  },
  {
    icon: CoinIcon,
    description: "Catlog is very affordable, we want to support your business, not kill it",
  },
  {
    icon: InvoiceIcon,
    description: "On Catlog you can create an invoice link, with multiple payment options",
  },
  {
    icon: SearchStatusIcon,
    description: "Catlog helps people find you from Google, with SEO optimisations for your store",
  },
  {
    icon: HashtagIcon,
    description: "With Catlog, Customers can find your products on twitter by just using a hashtag",
  },
];

export default ReasonsToUseCatlog;
